import {
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
} from "react";

import { CloseButton } from "@vega-core/components/Buttons/CloseButton/CloseButton";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { SidebarModalHeader } from "./SidebarModalHeader";

type Props = {
  actionPanel?: ReactNode;
  isClosing: boolean;
  isOpen: boolean;
  onClose: VoidFunction;
  noPadding: boolean;
  noScrolling?: boolean;
  dataTestId: string;
  title?: string;
  headerAction?: ReactNode;
};

export function SidebarModalPanelV2({
  actionPanel,
  children,
  isClosing,
  isOpen,
  onClose,
  noPadding,
  noScrolling = false,
  dataTestId,
  title,
  headerAction,
}: PropsWithChildren<Props>) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen && isTruthy(panelRef.current?.scrollTop)) {
      // if panel is scrolled from previous usage
      panelRef.current.scrollTo(0, 0);
    }
  }, [isOpen]);

  return (
    <div
      className={cnTwMerge(
        "sidebar-modal-panel bg-background/primary border-2 border-border/secondary w-[100vw] flex flex-col sm:w-sidebar-modal sm:right-2 max-h-[100dvh]",
        {
          hidden: !isOpen,
          "sidebar-closed": isClosing,
          "sidebar-open": isOpen && !isClosing,
          "!overflow-hidden": noScrolling,
        },
      )}
      data-testid={`${dataTestId}-side-panel`}
      onClick={(e) => e.stopPropagation()}
      style={{ height: "100vh" }}
    >
      <div className="flex-shrink-0">
        {!title ? (
          <div
            className={cnTwMerge(
              "float-right w-8 cursor-pointer text-text/primary",
              {
                "mt-4 mr-4 sm:mt-6 sm:mr-6": noPadding,
                "mt-6 mr-6": !noPadding,
              },
            )}
          >
            <CloseButton onClick={onClose} />
          </div>
        ) : null}

        {title ? (
          <SidebarModalHeader
            headerAction={headerAction}
            onClose={onClose}
            title={title}
          />
        ) : null}
      </div>

      <div
        className={cnTwMerge("flex-1 min-h-0", {
          "overflow-scroll overflow-x-hidden": !noScrolling,
        })}
        ref={panelRef}
      >
        <div
          className={cnTwMerge({
            "p-6": !noPadding,
            "p-0": noPadding,
            "h-full": noScrolling,
          })}
        >
          {children}
        </div>
      </div>

      {actionPanel ? (
        <div className="flex-shrink-0 border-t-2 border-border/secondary p-6 sidebar-bottom-panel">
          {actionPanel}
        </div>
      ) : null}
    </div>
  );
}
